import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getAllDrone,
  getDronesByCategoryId,
  getDronesParts,
} from "../../services/DroneServices";
import Fuse from "fuse.js"; // Make sure to import Fuse here
import { Col, Row, CloseButton } from "react-bootstrap";
import Slider from "rc-slider";
import "rc-slider/assets/index.css"; // Import slider CSS
import SearchIcon from "../../assets/image/svg/search_icon.svg";
import categoryIcon from "../../assets/image/svg/categoriesIcon.svg";
import priceFilterIcon from "../../assets/image/svg/priceFilterIcon.svg";
import debounce from "lodash/debounce";
import madeinindia from "../../assets/image/png/Madeinindia2.png";
import typeCertified from "../../assets/image/png/type-certified.png";

const AgricultureDrone = () => {
  // **State Variables**
  const [drones, setDrones] = useState([]);
  const originalDrones = useRef([]);
  const { id } = useParams();
  const [dronePage, setDronePage] = useState(1);
  const [droneTotalPage, setDroneTotalPage] = useState(1);
  const [droneCategory, setDroneCategory] = useState("All Drones");
  const [selectedImage, setSelectedImage] = useState(null);
  const [model, setModel] = useState(false);
  const [droneDetails, setDroneDetails] = useState(null);
  const [specifications, setSpecifications] = useState([]);
  const [allDroneParts, setAllDroneParts] = useState([]);
  const [partsPage, setPartsPage] = useState(1);
  const [partsTotalPage, setPartsTotalPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState(""); // Declare sort state here
  // const [page, setPage] = useState(1);

  // **Filter States**
  const [search, setSearch] = useState("");
  const [priceRange, setPriceRange] = useState([1000, 5000000]);
  const [minPrice, setMinPrice] = useState(10000);
  const [maxPrice, setMaxPrice] = useState(5000000);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [sortOption, setSortOption] = useState("default");
  const [showFilters, setShowFilters] = useState(false); // To toggle filter visibility
  const priceOptions = Array.from({ length: 100 }, (_, i) => (i + 1) * 15000);

  const [madeInIndiaOnly, setMadeInIndiaOnly] = useState(false);
  const [typeCertifiedOnly, setTypeCertifiedOnly] = useState(false);
  const [bestsellerOnly, setBestsellerOnly] = useState(false);
  const [weightFilter, setWeightFilter] = useState(""); // "", "under250", "250to2000", "over2000"

  // Fisher-Yates shuffle algorithm
  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  useEffect(() => {
    getDrones();
  }, [dronePage]);

  function getDrones() {
    if (id !== "all-drone" && id && dronePage !== 1) {
      getDronesByCategoryId({
        id: id,
        limit: 2000,
        page: dronePage,
        sort: sort,
        priceRange: priceRange,
      }).then((res) => {
        if (res.status === "success") {
          setDrones((prevDrones) => [...prevDrones, ...res.data.data]);
          if (res.data.totalPages !== 0) {
            setDroneTotalPage(res.data.totalPages);
          }
          if (res.data.data[0]?.categoryId?.name) {
            setDroneCategory(res.data.data[0]?.categoryId?.name);
          }
        }
      });
    } else if (dronePage !== 1) {
      getAllDrone({
        page: dronePage,
        limit: 20,
        sort: sort,
        priceRange: priceRange,
      })
        .then((res) => {
          setDrones((prevDrones) => [...prevDrones, ...res.data.data]);
          if (res.data.totalPages !== 1) {
            setDroneTotalPage(res.data.totalPages);
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }

  // Add this function to filter drones based on all criteria
  const filterDrones = (drones) => {
    // Step 1: Apply all filters
    let filtered = drones.filter((drone) => {
      const priceInRange =
        drone.price >= priceRange[0] && drone.price <= priceRange[1];
      const madeInIndiaMatch = !madeInIndiaOnly || drone.madeInIndia;
      const typeCertifiedMatch = !typeCertifiedOnly || drone.typeCertified;
      const bestsellerMatch = !bestsellerOnly || drone.bestseller;

      let weightMatch = true;
      if (weightFilter) {
        const weightSpec = drone.specifications?.find((spec) =>
          spec.name.toLowerCase().includes("weight"),
        );

        if (weightSpec) {
          const weightValue = parseFloat(weightSpec.value.split(" ")[0]);
          const weightGrams = weightValue * 1000;

          switch (weightFilter) {
            case "under250":
              weightMatch = weightGrams < 250;
              break;
            case "250to2000":
              weightMatch = weightGrams >= 250 && weightGrams <= 2000;
              break;
            case "over2000":
              weightMatch = weightGrams > 2000;
              break;
            default:
              weightMatch = true;
          }
        } else {
          weightMatch = weightFilter === "";
        }
      }

      return (
        priceInRange &&
        madeInIndiaMatch &&
        typeCertifiedMatch &&
        bestsellerMatch &&
        weightMatch
      );
    });

    // Step 2: Apply sorting
    if (sortOption === "price-asc") {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortOption === "price-desc") {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortOption === "newest") {
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return filtered;
  };

  useEffect(() => {
    const fetchFilteredDrones = async (isLoadMore = false) => {
      try {
        const filters = {
          search,
          brand: selectedBrand,
          page: isLoadMore ? dronePage + 1 : 1,
          limit: 2000,
          sort: sortOption,
          priceRange: priceRange,
          madeInIndia: madeInIndiaOnly || undefined,
          typeCertified: typeCertifiedOnly || undefined,
          bestseller: bestsellerOnly || undefined,
        };

        let response =
          id !== "all-drone" && id
            ? await getDronesByCategoryId({ id, ...filters })
            : await getAllDrone(filters);

        if (response.status === "success") {
          let filteredDrones = response.data.data;

          // Only shuffle if no filters are active
          const isAnyFilterActive =
            search ||
            selectedBrand ||
            sortOption !== "default" ||
            madeInIndiaOnly ||
            typeCertifiedOnly ||
            bestsellerOnly ||
            weightFilter ||
            priceRange[0] !== 1000 ||
            priceRange[1] !== 5000000;

          if (!isAnyFilterActive) {
            filteredDrones = shuffleArray(filteredDrones);
          } else {
            filteredDrones = filterDrones(filteredDrones);
          }

          setDrones(
            isLoadMore ? [...drones, ...filteredDrones] : filteredDrones,
          );
          setDronePage(filters.page);
        }
      } catch (error) {
        setError("An error occurred while fetching drones");
      }
    };
    fetchFilteredDrones();
  }, [priceRange, sortOption]);

  useEffect(() => {
    setDrones([]); // Reset drones when category changes
    setDronePage(1); // Reset to the first page
    setDroneTotalPage(1); // Reset total pages

    const fetchData = async () => {
      try {
        let response;
        if (id !== "all-drone" && id) {
          response = await getDronesByCategoryId({
            id: id,
            limit: 2000,
            page: 1,
          });
        } else {
          response = await getAllDrone({
            page: 1,
            limit: 2000,
          });
        }

        if (response.status === "success") {
          // Shuffle the drones before setting them
          const shuffledDrones = shuffleArray(response.data.data);
          setDrones(shuffledDrones);

          if (response.data.totalPages !== 0) {
            setDroneTotalPage(response.data.totalPages);
          }

          if (
            id !== "all-drone" &&
            id &&
            response?.data?.data[0]?.categoryId?.name
          ) {
            setDroneCategory(response?.data?.data[0]?.categoryId?.name);
          }
        }
      } catch (error) {
        console.error("Error fetching drones:", error);
      }
    };

    fetchData();
  }, [id]); // Add `id` as a dependency

  useEffect(() => {
    setDrones([]);
    if (sort) {
      setDronePage(1);
      if (id !== "all-drone" && id) {
        getDronesByCategoryId({
          id: id,
          limit: 2000,
          page: 1,
          sort: sort,
        }).then((res) => {
          if (res.status === "success") {
            setDrones((prevDrones) => [...prevDrones, ...res.data.data]);
            if (res.data.totalPages !== 0) {
              setDroneTotalPage(res.data.totalPages);
            }
            if (res.data.data[0]?.categoryId?.name) {
              setDroneCategory(res.data.data[0]?.categoryId?.name);
            }
          }
        });
      } else {
        getAllDrone({ page: 1, limit: 20, sort: sort })
          .then((res) => {
            setDrones((prevDrones) => [...prevDrones, ...res.data.data]);
            if (res.data.totalPages !== 0) {
              setDroneTotalPage(res.data.totalPages);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }, [sort]);

  useEffect(() => {
    fetchDroneParts();
  }, [partsPage]);

  const fetchDroneParts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getDronesParts({
        limit: 20,
        page: partsPage,
        search: "",
      });
      if (response.status === "success") {
        setAllDroneParts((prevAllDroneParts) => [
          ...prevAllDroneParts,
          ...response.data.droneParts,
        ]);
        if (response.data.totalPages !== 0) {
          setPartsTotalPage(response.data.totalPages);
        }
      } else {
        setError("Failed to fetch drone parts");
      }
    } catch (error) {
      setError("An error occurred while fetching drone parts");
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailClick = (thumbnail) => {
    setSelectedImage(thumbnail);
  };

  // **Effect to Fetch Drones Based on Filters and Pagination**
  useEffect(() => {
    // fetchDrones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dronePage, sortOption, search, minPrice, maxPrice, selectedBrand, id]);

  // **Function to Fetch Drones with Filters**

  // **Effect to Fetch Drone Parts**
  useEffect(() => {
    fetchDroneParts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partsPage]);

  // **Filter Handlers**
  // const handleSearch = (e) => {
  //   setSearch(e.target.value);
  // };
  // const handleSearch = (e) => {

  // };

  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearch(query); // Update the search query state

    // Prepare the common parameters for fetching drones
    const fetchParams = {
      page: 1,
      limit: 2000,
      sort: sortOption,
      priceRange: priceRange,
    };

    // If there's no query, fetch drones for the selected category or all drones based on category
    if (query === "") {
      try {
        let fullDrones;

        // Fetch drones based on category (if 'id' is defined)
        if (id && id !== "all-drone") {
          fullDrones = await getDronesByCategoryId({
            id: id,
            ...fetchParams,
          });
        } else {
          fullDrones = await getAllDrone({
            ...fetchParams,
          });
        }

        if (Array.isArray(fullDrones.data.data)) {
          setDrones(fullDrones.data.data); // Set the drones array
          setDroneTotalPage(fullDrones.data.totalPages); // Set total pages for pagination
        } else {
          console.error("Drones are not an array:", fullDrones.data.data);
        }
      } catch (error) {
        console.error("Error fetching drones:", error); // Log error if fetching fails
      }
    } else {
      // Perform fuzzy search when there's a search query
      try {
        let fullDrones;

        // Fetch drones based on category (if 'id' is defined)
        if (id && id !== "all-drone") {
          fullDrones = await getDronesByCategoryId({
            id: id,
            ...fetchParams,
          });
        } else {
          fullDrones = await getAllDrone({
            ...fetchParams,
          });
        }

        if (Array.isArray(fullDrones.data.data)) {
          // Initialize Fuse.js with options
          const fuse = new Fuse(fullDrones.data.data, {
            keys: ["name"], // The fields to search in (e.g., 'name' of the drone)
            threshold: 0.3, // This determines how fuzzy the search should be (0.3 is fairly forgiving)
          });

          // Perform the search
          const results = fuse.search(query);
          const matchedDrones = results.map((result) => result.item); // Extract the matched items

          setDrones(matchedDrones); // Set the matched drones array
          setDroneTotalPage(fullDrones.data.totalPages); // Set total pages for pagination
        } else {
          console.error("Drones are not an array:", fullDrones.data.data);
        }
      } catch (error) {
        console.error("Error fetching drones:", error); // Log error if fetching fails
      }
    }
  };

  // Render part - Make sure drones is an array before using .map()
  // {
  //   Array.isArray(drones) ? (
  //     drones.map(drone => {
  //       // render drone
  //     })
  //   ) : (
  //     <div>No drones available.</div> // Fallback UI
  //   );
  // }

  // Debounced filter function
  const debouncedFilter = useCallback(
    debounce((query) => {
      if (query === "") {
        // When the query is empty, reset to the full list of drones
        getAllDrone({
          page: 1,
          limit: 20,
          sort: sortOption,
          priceRange: priceRange,
        })
          .then((response) => {
            if (Array.isArray(response.data)) {
              setDrones(response.data); // Set full drone list
            } else {
              console.error("Fetched drones data is not an array:", response);
            }
          })
          .catch((error) => {
            console.error("Error fetching drones:", error);
          });
      } else {
        // Filter drones based on the search query
        const filtered = drones.filter((drone) =>
          drone.name.toLowerCase().includes(query.toLowerCase()),
        );
        setDrones(filtered); // Update filtered drones state
      }
    }, 500), // Debounced delay
    [drones], // Only recreate the debounce function when drones change
  );

  // Handle min price change
  const handleMinPriceChange = (e) => {
    const value = parseInt(e.target.value);
    if (value <= maxPrice) {
      setMinPrice(value);
      setPriceRange([value, maxPrice]);
    }
  };

  // Handle max price change
  const handleMaxPriceChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= minPrice) {
      setMaxPrice(value);
      setPriceRange([minPrice, value]);
    }
  };

  const handleBrandSelection = (brand) => {
    const updatedBrand = brand === selectedBrand ? "" : brand;
    setSelectedBrand(updatedBrand);
    setDronePage(1);
  };
  // Adjust the Clear Filters function
  const handleClearFilter = () => {
    setSearch("");
    setMinPrice(1);
    setMaxPrice(5000000);
    setSelectedBrand("");
    setSortOption("default");
    setDronePage(1);
    setPriceRange([1000, 5000000]);
    setMadeInIndiaOnly(false);
    setTypeCertifiedOnly(false);
    setBestsellerOnly(false);
    setWeightFilter("");

    // Re-fetch and shuffle drones when clearing filters
    const fetchData = async () => {
      try {
        let response;
        if (id !== "all-drone" && id) {
          response = await getDronesByCategoryId({
            id: id,
            limit: 2000,
            page: 1,
          });
        } else {
          response = await getAllDrone({
            page: 1,
            limit: 2000,
          });
        }

        if (response.status === "success") {
          const shuffledDrones = shuffleArray(response.data.data);
          setDrones(shuffledDrones);
        }
      } catch (error) {
        console.error("Error fetching drones:", error);
      }
    };

    fetchData();
  };
  // **Handle Sort Change (Unified for Dropdown and Buttons)**
  const handleSortChange = (sortOption) => {
    setSortOption(sortOption);
  };
  return (
    <div id="drones" className="py-0">
      <div className="container pb-md-5">
        {/* Header Section */}
        <div className="d-sm-flex align-items-start justify-content-between mb-4 sticky top-4">
          <div className="text-center text-sm-start">
            {" "}
            {/* 👈 Add responsive alignment */}
            <h3 className="ff_inter fs_43 fw-bold color_darkgray mb-0">
              {droneCategory}
            </h3>
            <p className="fs_24 fw-medium mb-4 mb-sm-0 ff_inter color_darkgray mt-1">
              View, Compare & Buy Drones
            </p>
          </div>
        </div>
        {/* Main Content: Filters and Drones */}
        <Row>
          {/* **Left Column: Filters** */}
          <Col lg={3} className="mb-4">
            {/* Filter Sidebar */}
            <div className="sticky top-28 z-10">
              {/* Horizontal scroll container for mobile */}
              <div className="lg:hidden overflow-x-auto pb-2 mb-4 hide-scrollbar">
                <div className="w-max flex space-x-2 px-2">
                  {/* Mobile filter chips */}
                  <button
                    onClick={() => setMadeInIndiaOnly(!madeInIndiaOnly)}
                    className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${madeInIndiaOnly ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"}`}
                  >
                    Made in India
                  </button>
                  <button
                    onClick={() => setTypeCertifiedOnly(!typeCertifiedOnly)}
                    className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${typeCertifiedOnly ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"}`}
                  >
                    Type Certified
                  </button>
                  <button
                    onClick={() => setBestsellerOnly(!bestsellerOnly)}
                    className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${bestsellerOnly ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"}`}
                  >
                    Bestseller
                  </button>
                  <select
                    value={weightFilter}
                    onChange={(e) => setWeightFilter(e.target.value)}
                    className="px-3 py-1 rounded-full text-sm bg-gray-100 border-0 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Weights</option>
                    <option value="under250">Under 250g</option>
                    <option value="250to2000">250g-2kg</option>
                    <option value="over2000">Over 2kg</option>
                  </select>
                  <button
                    onClick={handleClearFilter}
                    className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 whitespace-nowrap"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {/* Vertical scroll container for desktop */}
              <div className="hidden lg:block h-[calc(100vh-140px)] overflow-y-auto">
                <div className="bg-white rounded-lg shadow-sm p-4">
                  {/* Search Box */}
                  <div className="mb-4">
                    <form
                      onSubmit={(e) => e.preventDefault()}
                      className="relative"
                    >
                      <input
                        type="text"
                        value={search}
                        onChange={handleSearch}
                        placeholder="Search Drones"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <img
                          src={SearchIcon}
                          alt="Search"
                          className="w-5 h-5 text-gray-400"
                        />
                      </div>
                    </form>
                  </div>

                  {/* Price Range Filter */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-base font-medium text-gray-800">
                        Price Range
                      </h4>
                    </div>

                    <Slider
                      range
                      allowCross={false}
                      min={1}
                      max={5000000}
                      value={priceRange}
                      onChange={setPriceRange}
                      className="mb-4"
                    />

                    <div className="flex justify-between text-sm text-gray-600 mb-3">
                      <span>₹{priceRange[0].toLocaleString("en-IN")}</span>
                      <span>₹{priceRange[1].toLocaleString("en-IN")}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Min Price
                        </label>
                        <select
                          value={minPrice}
                          onChange={handleMinPriceChange}
                          className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        >
                          {priceOptions.map((price) => (
                            <option key={price} value={price}>
                              ₹{price.toLocaleString("en-IN")}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Max Price
                        </label>
                        <select
                          value={maxPrice}
                          onChange={handleMaxPriceChange}
                          className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        >
                          {priceOptions.map((price) => (
                            <option key={price} value={price}>
                              ₹{price.toLocaleString("en-IN")}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Additional Filters */}
                  <div className="space-y-4">
                    {/* Made in India Toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <label
                        htmlFor="madeInIndiaCheck"
                        className="text-sm font-medium text-gray-700 cursor-pointer"
                      >
                        Made in India Only
                      </label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="madeInIndiaCheck"
                          className="sr-only peer"
                          checked={madeInIndiaOnly}
                          onChange={() => setMadeInIndiaOnly(!madeInIndiaOnly)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {/* Type Certified Toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <label
                        htmlFor="typeCertifiedCheck"
                        className="text-sm font-medium text-gray-700 cursor-pointer"
                      >
                        Type Certified Only
                      </label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="typeCertifiedCheck"
                          className="sr-only peer"
                          checked={typeCertifiedOnly}
                          onChange={() =>
                            setTypeCertifiedOnly(!typeCertifiedOnly)
                          }
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    {/* Bestseller Toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <label
                        htmlFor="bestsellerCheck"
                        className="text-sm font-medium text-gray-700 cursor-pointer"
                      >
                        Bestseller Only
                      </label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="bestsellerCheck"
                          className="sr-only peer"
                          checked={bestsellerOnly}
                          onChange={() => setBestsellerOnly(!bestsellerOnly)}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {/* Weight Filter */}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Weight Range
                      </h4>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          onClick={() =>
                            setWeightFilter(
                              weightFilter === "under250" ? "" : "under250",
                            )
                          }
                          className={`py-2 text-xs rounded-md ${weightFilter === "under250" ? "bg-blue-600 text-white" : "bg-white border border-gray-300 text-gray-700"}`}
                        >
                          &lt;250g
                        </button>
                        <button
                          onClick={() =>
                            setWeightFilter(
                              weightFilter === "250to2000" ? "" : "250to2000",
                            )
                          }
                          className={`py-2 text-xs rounded-md ${weightFilter === "250to2000" ? "bg-blue-600 text-white" : "bg-white border border-gray-300 text-gray-700"}`}
                        >
                          250g-2kg
                        </button>
                        <button
                          onClick={() =>
                            setWeightFilter(
                              weightFilter === "over2000" ? "" : "over2000",
                            )
                          }
                          className={`py-2 text-xs rounded-md ${weightFilter === "over2000" ? "bg-blue-600 text-white" : "bg-white border border-gray-300 text-gray-700"}`}
                        >
                          &gt;2kg
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>

          {/* **Right Column: Drones and Drone Parts Display** */}
          <Col lg={9}>
            {/* **Sort By Section (Buttons) */}
            <div className="flex flex-wrap justify-between items-center mb-4">
              {/* Uncomment below if you want to show the results count */}
              {/* <div>
                  <h5 className="mb-0 text-base font-medium">Showing {drones.length} results</h5>
                </div> */}

              <div className="flex flex-wrap items-center gap-3 mt-2 lg:mt-0">
                <p className="mb-0 font-semibold text-sm">Sort By:</p>

                {/* Price Dropdown */}
                <div className="relative">
                  <select
                    className="block appearance-none w-full bg-white border border-gray-300 text-gray-700 py-1 px-3 pr-8 rounded-md text-sm leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={
                      sortOption === "price-asc" || sortOption === "price-desc"
                        ? sortOption
                        : ""
                    }
                    onChange={(e) => handleSortChange(e.target.value)}
                  >
                    <option value="" disabled>
                      Price
                    </option>
                    <option value="price-asc">Price - Low to High</option>
                    <option value="price-desc">Price - High to Low</option>
                  </select>
                </div>

                {/* Newest First Button */}
                <button
                  className={`px-3 py-1 rounded-md border text-sm transition ${
                    sortOption === "newest"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
                  onClick={() => handleSortChange("newest")}
                >
                  Newest First
                </button>
              </div>
            </div>

            {/* **Drones Listing** */}
            <div className="row">
              {filterDrones(drones).length === 0 && !loading && !error && (
                <div className="col-12">
                  <p className="text-center">
                    No drones found matching your criteria.
                  </p>
                </div>
              )}

              {filterDrones(drones)?.map((obj, index) => (
                <div key={index} className="col-lg-4 col-md-20 col-sm-12 mt-4">
                  {obj.outOfStock ? (
                    <div
                      className="product-item-content drone-card d-flex flex-column justify-content-between h-100 position-relative"
                      style={{
                        border: "2px solid #ef4444", // red
                        opacity: 0.85,
                      }}
                    >
                      {/* Out of Stock Overlay */}
                      <div className="position-absolute top-0 start-0 w-100 h-100 bg-light opacity-50 z-index-3"></div>

                      {/* Out of Stock Badge */}
                      <div
                        className="position-absolute top-0 start-0 z-index-3 d-flex align-items-center px-3 py-1"
                        style={{
                          background:
                            "linear-gradient(135deg, #b00020, #ff1744)",
                          color: "white",
                          fontWeight: "600",
                          fontSize: "14px",
                          height: "32px",
                          borderTopLeftRadius: "12px",
                          borderBottomRightRadius: "12px",
                        }}
                      >
                        Out of Stock
                      </div>

                      {/* Image Container */}
                      <div style={{ position: "relative" }}>
                        {/* Made in India Logo (left side) */}
                        {obj.madeInIndia && (
                          <img
                            src={madeinindia}
                            alt="Made in India"
                            style={{
                              position: "absolute",
                              left: "10px",
                              top: "10px",
                              height: "40px",
                              zIndex: 2,
                            }}
                          />
                        )}

                        {/* Type Certified Logo (right side) */}
                        {obj.typeCertified && (
                          <img
                            src={typeCertified}
                            alt="Type Certified"
                            style={{
                              position: "absolute",
                              right: "1px",
                              height: "10px",
                              zIndex: 2,
                            }}
                          />
                        )}

                        <img
                          height={200}
                          className="w-100 rounded-4 object-fit-contain"
                          src={obj?.images[0]}
                          alt="agricultureDrone_image"
                          loading="lazy"
                          style={{ opacity: 0.7 }}
                        />
                      </div>

                      <h4 className="fs_17 mt-3 fw-bold mb-0 ff_inter color_darkgray">
                        {obj?.name?.length > 50
                          ? `${obj?.name.slice(0, 50)}...`
                          : obj?.name}
                      </h4>
                      <p className="fs_17 fw-normal mb-2 ff_inter color_gray">
                        {obj?.description?.length > 20
                          ? `${obj?.description.slice(0, 20)}...`
                          : obj?.description}
                      </p>
                      <div className="mt-auto d-flex justify-content-between align-items-center mb-3">
                        <p className="price_tag_small ff_inter mb-0">
                          ₹
                          {Math.floor(obj?.approvedPrice).toLocaleString(
                            "en-IN",
                            { maximumFractionDigits: 0 },
                          )}
                        </p>
                        <button
                          className="viewall_btn bg-secondary px-4 py-2 fs_17 fw-medium ff_inter text-white text-nowrap"
                          disabled
                        >
                          Details
                        </button>
                      </div>
                    </div>
                  ) : (
                    <Link to={`/product-detail/${obj._id}`}>
                      <div
                        className="product-item-content drone-card d-flex flex-column justify-content-between h-100 position-relative"
                        style={{
                          border: "2px solid #328ccd", // blue-600
                        }}
                      >
                        {/* Image Container */}
                        <div style={{ position: "relative" }}>
                          {/* Made in India Logo (left side) */}
                          {obj.madeInIndia && (
                            <img
                              src={madeinindia}
                              alt="Made in India"
                              style={{
                                position: "absolute",
                                left: "10px",
                                top: "10px",
                                height: "40px",
                                zIndex: 2,
                              }}
                            />
                          )}

                          {/* Type Certified Logo (right side) */}
                          {obj.typeCertified && (
                            <img
                              src={typeCertified}
                              alt="Type Certified"
                              style={{
                                position: "absolute",
                                right: "1px",
                                top: "10px",
                                height: "20px",
                                zIndex: 2,
                              }}
                            />
                          )}

                          <img
                            height={200}
                            className="w-100 rounded-4 object-fit-contain"
                            src={obj?.images[0]}
                            alt="agricultureDrone_image"
                            loading="lazy"
                          />
                        </div>

                        <h4 className="fs_17 mt-3 fw-bold mb-0 ff_inter color_darkgray">
                          {obj?.name?.length > 50
                            ? `${obj?.name.slice(0, 50)}...`
                            : obj?.name}
                        </h4>
                        <p className="fs_17 fw-normal mb-2 ff_inter color_gray">
                          {obj?.description?.length > 20
                            ? `${obj?.description.slice(0, 20)}...`
                            : obj?.description}
                        </p>
                        <div className="mt-auto d-flex justify-content-between align-items-center mb-3">
                          <p className="price_tag_small ff_inter mb-0">
                            ₹
                            {Math.floor(obj?.approvedPrice).toLocaleString(
                              "en-IN",
                              { maximumFractionDigits: 0 },
                            )}
                          </p>
                          <button className="viewall_btn bg_light_blue px-4 py-2 fs_17 fw-medium ff_inter text-white text-nowrap">
                            Details
                          </button>
                        </div>
                      </div>
                    </Link>
                  )}
                </div>
              ))}

              {/* Loading Indicator for Drones */}
              {loading && (
                <div className="col-12 d-flex justify-content-center">
                  <p>Loading...</p>
                </div>
              )}

              {/* Error Message for Drones */}
              {error && (
                <div className="col-12 d-flex justify-content-center">
                  <p className="text-danger">{error}</p>
                </div>
              )}

              {/* Load More Button for Drones */}
              {dronePage < droneTotalPage && !loading && (
                <div className="col-12 d-flex justify-content-center">
                  <button
                    onClick={() => setDronePage((prevPage) => prevPage + 1)}
                    className="tab_btn px-4 py-2 border-0 rounded-2 mt-5 fs_13 ff_inter fw-semibold text-white"
                  >
                    Load More
                  </button>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};
export default AgricultureDrone;
